import type { Cart, LineItem, Order } from "@commercetools/platform-sdk";
import type { WindowLocation } from "@reach/router";
import type { TCountryCode } from "countries-list";
import * as FourOhFour from "./events/404";
import * as addToCart from "./events/add-to-cart";
import * as beginCheckout from "./events/begin-checkout";
import * as productDetail from "./events/product-detail";
import * as purchase from "./events/purchase";
import * as removeFromCart from "./events/remove-from-cart";
import { pushEvent } from "./gtm";

type PageType =
	| "purchase"
	| "cart"
	| "product"
	| "category"
	| "checkout"
	| "homepage";

type PushCustomGTMEventArgs =
	| {
			type: "beginCheckout";
			data: {
				cart: Cart;
			};
	  }
	| { type: "cartPreview" }
	| { type: "checkBillingAddress" }
	| { type: "customerDataAdded" }
	| { type: "deliveryMethodAdded" }
	| { type: "goToCart" }
	| { type: "goToCheckout" }
	| { type: "paymentInProgress" }
	| { type: "purchase"; data: { order: Order } }
	| {
			type: "addToCart";
			data: { lineItems: LineItem[] };
	  }
	| {
			type: "removeFromCart";
			data: { lineItem: LineItem };
	  }
	| { type: "setPageType"; data: { pageType: PageType } }
	| {
			type: "productDetail";
			data: {
				variant: Queries.ProductVariantFragment;
				product: Queries.CommerceToolsProductFragment;
				currency: string;
				country?: TCountryCode;
			};
	  }
	| { type: "404"; data: { location: WindowLocation } }
	| {
			type: "tabHidden";
	  }
	| { type: "tabVisible" };

export const pushCustomGTMEvent = (event: PushCustomGTMEventArgs): void => {
	switch (event.type) {
		case "beginCheckout": {
			pushEvent({
				event: "begin_checkout",
				...beginCheckout.getData(event.data.cart),
			});
			break;
		}

		case "cartPreview":
		case "checkBillingAddress":
		case "customerDataAdded":
		case "deliveryMethodAdded":
		case "goToCart":
		case "goToCheckout":
		case "paymentInProgress": {
			pushEvent({
				event: event.type,
			});
			break;
		}

		case "purchase": {
			pushEvent({ event: event.type, ...purchase.getData(event.data.order) });
			break;
		}

		case "setPageType": {
			pushEvent({ pagetype: event.data.pageType });
			break;
		}

		case "addToCart": {
			pushEvent({
				event: "AddToCart",
				...addToCart.getData(event.data.lineItems),
			});
			break;
		}

		case "productDetail": {
			pushEvent({
				event: event.type,
				...productDetail.getData(
					event.data.variant,
					event.data.product,
					event.data.currency,
					event.data.country,
				),
			});
			break;
		}

		case "removeFromCart": {
			pushEvent({
				event: event.type,
				...removeFromCart.getData(event.data.lineItem),
			});
			break;
		}
		case "404": {
			pushEvent({
				event: "404 error",
				...FourOhFour.getData(event.data.location),
			});
			break;
		}
	}
};
